import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Lock } from "lucide-react";

export default function Privacy() {
  const { lang } = useLang();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="overline text-secondary flex items-center gap-1.5"><Lock size={12} /> Privacy</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "गोपनीयता नीति" : "Privacy Policy"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "hi" ? "अंतिम अपडेट: फ़रवरी 2026" : "Last updated: February 2026"}
        </p>
      </div>

      <div className="space-y-6 text-sm leading-relaxed" data-testid="privacy-content">
        <section>
          <h2 className="text-lg font-semibold text-primary">1. {lang === "hi" ? "हम क्या इकट्ठा करते हैं" : "What we collect"}</h2>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>{lang === "hi" ? "खाता विवरण: नाम, ईमेल, फ़ोन (साइनअप के दौरान)" : "Account details: name, email, phone (during signup)"}</li>
            <li>{lang === "hi" ? "प्रॉपर्टी डेटा: फ़ोटो, विवरण, स्थान, दस्तावेज़ (लिस्टिंग बनाते समय)" : "Property data: photos, description, location, papers (when you list)"}</li>
            <li>{lang === "hi" ? "पूछताछ: खरीदार का नाम, फ़ोन, पसंदीदा समय" : "Enquiries: buyer name, phone, preferred time"}</li>
            <li>{lang === "hi" ? "उपयोग: पेज व्यू, खोज क्वेरी (कुल गुमनाम)" : "Usage: page views, search queries (aggregate anonymous)"}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">2. {lang === "hi" ? "हम इसका उपयोग कैसे करते हैं" : "How we use it"}</h2>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>{lang === "hi" ? "खरीदारों और विक्रेताओं को BhoomiMitra प्रबंधन के माध्यम से जोड़ना" : "Connect buyers and sellers via BhoomiMitra Management"}</li>
            <li>{lang === "hi" ? "आपकी लिस्टिंग पर सर्च मैच के बारे में सूचित करना (WhatsApp/SMS)" : "Notify you about search matches on your listings (WhatsApp/SMS)"}</li>
            <li>{lang === "hi" ? "प्रॉपर्टी सिफ़ारिशें बेहतर बनाना" : "Improve property recommendations"}</li>
            <li>{lang === "hi" ? "क़ानूनी अनुपालन और धोखाधड़ी की रोकथाम" : "Legal compliance and fraud prevention"}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">3. {lang === "hi" ? "गोपनीयता — डिफ़ॉल्ट रूप से" : "Privacy by default"}</h2>
          <p>
            {lang === "hi"
              ? "विक्रेता के फ़ोन नंबर सार्वजनिक रूप से कभी नहीं दिखाए जाते। खरीदार BhoomiMitra प्रबंधन को कॉल करते हैं जो कनेक्शन को सुगम बनाता है। यह दोनों पक्षों को स्पैम, धोखाधड़ी और अनचाहे कॉलों से बचाता है।"
              : "Seller phone numbers are never publicly displayed. Buyers call BhoomiMitra Management who brokers the connection. This protects both parties from spam, fraud, and unwanted calls."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">4. {lang === "hi" ? "आपके क़ानूनी दस्तावेज़" : "Your legal papers"}</h2>
          <p>
            {lang === "hi"
              ? "क़ानूनी दस्तावेज़ (उदा. रजिस्ट्री, पट्टा) एन्क्रिप्टेड ऑब्जेक्ट स्टोरेज में रखे जाते हैं। केवल BhoomiMitra खातों के साथ लॉग इन किए गए उपयोगकर्ता ही देख सकते हैं (डाउनलोड नहीं)। हम कभी भी दस्तावेज़ों को क्रेडिट स्कोर, सत्यापन एजेंसियों, या रीयल-एस्टेट एजेंटों को इस अनुमति के बिना नहीं बेचते।"
              : "Legal papers (e.g. registry, patta) are kept in encrypted object storage. Only logged-in users with BhoomiMitra accounts can view (not download). We never sell papers to credit-score bureaus, verification agencies, or real-estate agents without permission."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">5. {lang === "hi" ? "तीसरे-पक्ष के साथ साझाकरण" : "Third-party sharing"}</h2>
          <p>{lang === "hi" ? "हम केवल इनके साथ शेयर करते हैं:" : "We share only with:"}</p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>{lang === "hi" ? "पेमेंट प्रोसेसर (Razorpay) — केवल भुगतान डेटा" : "Payment processor (Razorpay) — payment data only"}</li>
            <li>{lang === "hi" ? "मैसेजिंग प्रदाता (Twilio/Meta WhatsApp) — फ़ोन डिलीवरी के लिए" : "Messaging provider (Twilio/Meta WhatsApp) — for phone delivery"}</li>
            <li>{lang === "hi" ? "क़ानूनी अनुरोध पर सरकार / अदालत" : "Government / court on legal request"}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">6. {lang === "hi" ? "आपके अधिकार" : "Your rights"}</h2>
          <p>
            {lang === "hi"
              ? "आप कभी भी अपना डेटा एक्सेस कर सकते हैं, सुधार सकते हैं, या डिलीट कर सकते हैं। हमें ईमेल करें: hello@bhoomimitra.in। हम भारतीय डिजिटल पर्सनल डेटा प्रोटेक्शन अधिनियम, 2023 (DPDPA) का पालन करते हैं।"
              : "You can access, correct, or delete your data at any time. Email us: hello@bhoomimitra.in. We comply with India's Digital Personal Data Protection Act, 2023 (DPDPA)."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">7. {lang === "hi" ? "कुकीज़" : "Cookies"}</h2>
          <p>
            {lang === "hi"
              ? "हम केवल आवश्यक कुकीज़ का उपयोग करते हैं: आपको लॉग इन रखने के लिए, और आपकी भाषा वरीयता याद रखने के लिए। कोई थर्ड-पार्टी विज्ञापन ट्रैकर्स नहीं।"
              : "We use essential cookies only: to keep you logged in, and to remember your language preference. No third-party ad trackers."}
          </p>
        </section>

        <p className="text-xs text-muted-foreground pt-4 border-t border-border">
          {lang === "hi" ? "प्रश्न?" : "Questions?"}{" "}
          <a href="mailto:hello@bhoomimitra.in" className="text-primary underline">hello@bhoomimitra.in</a>
          {" • "}
          <Link to="/terms" className="text-primary underline">{lang === "hi" ? "नियम और शर्तें" : "Terms"}</Link>
        </p>
      </div>
    </div>
  );
}
